import React, { useEffect, useMemo, useRef } from 'react';
import { Dayjs } from 'dayjs';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { SmartScheduleEvent } from '@/features/smartSchedule/model';
import EventCard from './EventCard';
import { getEventStart, getStatusGroup } from './utils';

type AgendaPanelProps = {
    title: string;
    events: SmartScheduleEvent[];
    now: Dayjs;
    variant?: 'default' | 'week';
    onClose: () => void;
    onOpenEvent: (event: SmartScheduleEvent) => void;
    onNavigateDay?: (offset: -1 | 1) => void;
    canNavigatePreviousDay?: boolean;
    canNavigateNextDay?: boolean;
};

const AgendaPanel = ({
    title,
    events,
    now,
    variant = 'default',
    onClose,
    onOpenEvent,
    onNavigateDay,
    canNavigatePreviousDay = true,
    canNavigateNextDay = true,
}: AgendaPanelProps) => {
    const closeRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    const hasDateNavigation = Boolean(onNavigateDay);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const appRoot = document.getElementById('root');
        const previousBodyOverflow = document.body.style.overflow;
        const previousAriaHidden = appRoot?.getAttribute('aria-hidden');
        const hadInert = appRoot?.hasAttribute('inert') || false;

        document.body.style.overflow = 'hidden';
        appRoot?.setAttribute('aria-hidden', 'true');
        appRoot?.setAttribute('inert', '');
        closeRef.current?.focus();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented) return;
            if (event.key === 'Escape') {
                onCloseRef.current();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(
                dialogRef.current?.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
                ) || []
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousBodyOverflow;
            if (previousAriaHidden === null || previousAriaHidden === undefined) appRoot?.removeAttribute('aria-hidden');
            else appRoot?.setAttribute('aria-hidden', previousAriaHidden);
            if (!hadInert) appRoot?.removeAttribute('inert');
            previousFocus?.focus();
        };
    }, []);

    useEffect(() => {
        if (!hasDateNavigation || !contentRef.current) return;
        contentRef.current.scrollTop = 0;
    }, [hasDateNavigation, title]);

    const sortedEvents = useMemo(
        () => [...events].sort((left, right) => getEventStart(left).valueOf() - getEventStart(right).valueOf()),
        [events]
    );
    const timeGroups = useMemo(() => {
        const groups: Array<{
            key: string;
            label: string;
            dateTime: string;
            events: SmartScheduleEvent[];
        }> = [];

        sortedEvents.forEach((event) => {
            const start = getEventStart(event);
            const key = start.format('YYYY-MM-DD-HH-mm');
            const previousGroup = groups[groups.length - 1];
            if (previousGroup?.key === key) {
                previousGroup.events.push(event);
                return;
            }
            groups.push({
                key,
                label: start.format('HH:mm'),
                dateTime: start.format('YYYY-MM-DDTHH:mm'),
                events: [event],
            });
        });

        return groups;
    }, [sortedEvents]);

    return createPortal(
        <div className="smart-schedule__drawer-backdrop" role="presentation" onMouseDown={onClose}>
            <aside
                ref={dialogRef}
                className={`smart-schedule__drawer smart-schedule__drawer--${variant}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="smart-schedule-agenda-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="smart-schedule__drawer-header">
                    <div className="smart-schedule__drawer-header-main">
                        <div
                            className={`smart-schedule__drawer-title-row${
                                onNavigateDay ? ' smart-schedule__drawer-title-row--navigable' : ''
                            }`}
                        >
                            <h3 id="smart-schedule-agenda-title" aria-live="polite">{title}</h3>
                            {onNavigateDay && (
                                <div className="smart-schedule__drawer-date-navigation" role="group" aria-label="일정 날짜 이동">
                                    <button
                                        type="button"
                                        aria-label="이전 날 일정 보기"
                                        title="이전 날"
                                        disabled={!canNavigatePreviousDay}
                                        onClick={() => onNavigateDay(-1)}
                                    >
                                        <ChevronLeft size={17} aria-hidden="true" />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label="다음 날 일정 보기"
                                        title="다음 날"
                                        disabled={!canNavigateNextDay}
                                        onClick={() => onNavigateDay(1)}
                                    >
                                        <ChevronRight size={17} aria-hidden="true" />
                                    </button>
                                </div>
                            )}
                        </div>
                        <p aria-live="polite">{events.length}개의 일정</p>
                    </div>
                    <button className="smart-schedule__drawer-close" ref={closeRef} type="button" aria-label="일정 목록 닫기" onClick={onClose}>
                        <X size={20} aria-hidden="true" />
                    </button>
                </div>
                <div ref={contentRef} className="smart-schedule__drawer-content">
                    {timeGroups.length === 0 && (
                        <div className="smart-schedule__agenda-empty" role="status">
                            <strong>등록된 일정이 없어요</strong>
                            <span>
                                {hasDateNavigation
                                    ? '날짜 이동 버튼으로 다른 날의 일정을 확인할 수 있어요.'
                                    : '현재 조건에 표시할 일정이 없어요.'}
                            </span>
                        </div>
                    )}
                    {timeGroups.map((group) => (
                        <section
                            key={group.key}
                            className="smart-schedule__agenda-time-group"
                            aria-labelledby={`smart-schedule-agenda-time-${group.key}`}
                        >
                            <h4
                                id={`smart-schedule-agenda-time-${group.key}`}
                                className="smart-schedule__agenda-time-heading"
                            >
                                <time dateTime={group.dateTime}>{group.label}</time>
                                <span>{group.events.length}개</span>
                                <i aria-hidden="true" />
                            </h4>
                            <div className="smart-schedule__agenda-time-events">
                                {group.events.map((event) => (
                                    <EventCard
                                        key={event.id}
                                        event={event}
                                        variant="agenda"
                                        status={getStatusGroup(event, now)}
                                        onQuickMoveOpen={onClose}
                                        onOpen={(selected) => {
                                            onClose();
                                            onOpenEvent(selected);
                                        }}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </aside>
        </div>,
        document.body
    );
};

export default AgendaPanel;
